import React, { Suspense, useState, useEffect } from 'react';
import { Admin, Resource, CustomRoutes } from 'react-admin';
import { Route } from 'react-router-dom';
import { StyledEngineProvider } from '@mui/material/styles';
import { CircularProgress, Box } from '@mui/material';

// Providers
import authProvider, { authEvents, AUTH_EVENTS } from './providers/authProvider';
import dataProvider from './providers/dataProvider';
import i18nProvider from './providers/i18nProvider';

// Layout con HotelProvider integrado

import { CustomLayout } from './layouts';

// Componentes
import CustomLoginPage from './components/auth/CustomLoginPage';
import Dashboard from './features/dashboard/components/Dashboard';
import NotAuthorized from './components/common/NotAuthorized';

// Rutas adicionales de Hotels
import { HotelZones, HotelTexts, HotelNotifications, HotelFields } from './features/hotels';

// Tema
import lightTheme from './lightTheme';

// Configuración de recursos
import { getResourcesForReactAdmin } from './config/resourceConfig';

const App: React.FC = () => {
  const [resources, setResources] = useState(() => getResourcesForReactAdmin());
  const [adminKey, setAdminKey] = useState(0);
  
  useEffect(() => {
    const reloadResources = () => {
      const newResources = getResourcesForReactAdmin();
      setResources(newResources);
      setAdminKey(prev => prev + 1);
    };
    
    const handleLoginSuccess = (event: Event) => {
      setTimeout(() => {
        reloadResources();
      }, 100);
    };
    
    const handleLogout = () => {
      reloadResources();
    };
    
    const handleRoleChanged = () => {
      reloadResources();
    };
    
    // Agregar listeners
    authEvents.addEventListener(AUTH_EVENTS.LOGIN_SUCCESS, handleLoginSuccess);
    authEvents.addEventListener(AUTH_EVENTS.LOGOUT, handleLogout);
    authEvents.addEventListener(AUTH_EVENTS.ROLE_CHANGED, handleRoleChanged);
    
    // Cleanup
    return () => {
      authEvents.removeEventListener(AUTH_EVENTS.LOGIN_SUCCESS, handleLoginSuccess);
      authEvents.removeEventListener(AUTH_EVENTS.LOGOUT, handleLogout);
      authEvents.removeEventListener(AUTH_EVENTS.ROLE_CHANGED, handleRoleChanged);
    };
  }, []);
  
  return (
    <Suspense 
      fallback={
        <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
          <CircularProgress />
        </Box>
      }
    >
      <StyledEngineProvider injectFirst>
        <Admin
          key={adminKey}
          authProvider={authProvider}
          dataProvider={dataProvider}
          i18nProvider={i18nProvider}
          theme={lightTheme}
          layout={CustomLayout}
          loginPage={CustomLoginPage}
          dashboard={Dashboard}
          
          requireAuth
          disableTelemetry
        >
          {/* Recursos dinámicos desde resourceConfig */}
          {resources.map(resource => (
            <Resource 
              key={resource.name}
              {...resource}
            />
          ))}

          {/* Rutas personalizadas */}
          <CustomRoutes>
            <Route path="/not-authorized" element={<NotAuthorized />} />
            
            {/* Rutas de configuración de Hotels */}
            <Route path="/hotels/:id/zones" element={<HotelZones />} />
            <Route path="/hotels/:id/texts" element={<HotelTexts />} />
            <Route path="/hotels/:id/notifications" element={<HotelNotifications />} />
            <Route path="/hotels/:id/fields" element={<HotelFields />} />
          </CustomRoutes>
        </Admin>
      </StyledEngineProvider>
    </Suspense>
  );
};

export default App;